import { Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ORIGIN_LABELS } from "./cfUtils";
import { TYPE_LABELS } from "./CustomFieldsDialog";

const inp = "bg-[#0A0E17] border-[#1F2937] h-8 text-slate-100";
const sel = "bg-[#0A0E17] border border-[#1F2937] rounded-md h-8 px-2 text-sm text-slate-100 w-full";

function TypedInput({ def, value, onChange }) {
  const k = def.field_key;
  if (def.type === "boolean") {
    return (
      <select data-testid={`custom-field-${k}`} value={value === true ? "si" : value === false ? "no" : ""} onChange={(e) => onChange(e.target.value === "" ? "" : e.target.value === "si")} className={sel}>
        <option value="">—</option><option value="si">Sì</option><option value="no">No</option>
      </select>
    );
  }
  if (def.type === "select") {
    return (
      <select data-testid={`custom-field-${k}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={sel}>
        <option value="">—</option>{(def.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        {value && !(def.options || []).includes(value) && <option value={value}>{value} (non ammesso)</option>}
      </select>
    );
  }
  if (def.type === "multiselect") {
    const vals = Array.isArray(value) ? value : value ? String(value).split(/[;|]/).map((s) => s.trim()).filter(Boolean) : [];
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={`custom-field-${k}`}>
        {(def.options || []).map((o) => <button key={o} type="button" data-testid={`custom-field-${k}-opt-${o}`} onClick={() => onChange(vals.includes(o) ? vals.filter((x) => x !== o) : [...vals, o])}
          className={`px-2 py-0.5 rounded-lg text-[11px] border ${vals.includes(o) ? "bg-[#10B981] border-[#10B981] text-[#0A0E17] font-semibold" : "border-[#1F2937] text-slate-400"}`}>{o}</button>)}
      </div>
    );
  }
  const type = def.type === "number" ? "number" : def.type === "date" ? "date" : "text";
  return <Input type={type} step={def.type === "number" ? "any" : undefined} data-testid={`custom-field-${k}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inp} placeholder={def.description || TYPE_LABELS[def.type]} />;
}

/** Sezione «Campi personalizzati» della scheda cliente: campi definiti (tipizzati), archiviati (sola lettura) e chiavi libere senza definizione. */
export function CustomFieldsSection({ defs, values, onChange, onRemove, newCustom, setNewCustom, onAdd }) {
  const v = values || {};
  const byKey = Object.fromEntries((defs || []).map((d) => [d.field_key, d]));
  const visible = (defs || []).filter((d) => d.active && d.show_in_contact !== false);
  const archivedWithValue = (defs || []).filter((d) => !d.active && v[d.field_key] !== undefined && v[d.field_key] !== "");
  const free = Object.keys(v).filter((k) => !byKey[k]);
  const Origin = ({ d }) => { const [l, c] = ORIGIN_LABELS[d.origin || (d.adopted_legacy ? "legacy" : "manual")] || ORIGIN_LABELS.manual; return <span className={`text-[10px] px-1.5 py-0.5 rounded ${c}`} data-testid={`cf-origin-${d.field_key}`}>{l}</span>; };
  return (
    <div className="space-y-2" data-testid="custom-fields-section">
      <div className="flex items-center justify-between"><span className="text-sm font-medium">Campi personalizzati</span>
        <span className="text-[10px] text-slate-500">campi di sistema sopra · <span className="text-emerald-700">manuale</span> · <span className="text-sky-700">da import</span> · <span className="text-amber-700">adottato</span> · <span className="text-slate-400">libero</span></span></div>
      {!visible.length && !archivedWithValue.length && !free.length && <p className="text-xs text-slate-500" data-testid="custom-fields-empty">Nessun campo personalizzato. Definiscili da «Impostazioni anagrafica» oppure vengono creati da import e automazioni.</p>}
      {visible.map((d) => (
        <div key={d.field_key} className="grid grid-cols-[8rem_1fr] sm:grid-cols-[10rem_1fr] items-center gap-2" data-testid={`cf-row-${d.field_key}`}>
          <div className="min-w-0"><p className="text-xs text-slate-300 break-words line-clamp-2" title={`${d.label} · ${d.field_key} · ${TYPE_LABELS[d.type]}`}>{d.label}{d.required && <span className="text-amber-400"> *</span>}</p><Origin d={d} /></div>
          <TypedInput def={d} value={v[d.field_key]} onChange={(val) => onChange(d.field_key, val)} />
        </div>
      ))}
      {archivedWithValue.map((d) => (
        <div key={d.field_key} className="grid grid-cols-[8rem_1fr] sm:grid-cols-[10rem_1fr] items-center gap-2 opacity-70" data-testid={`cf-row-archived-${d.field_key}`}>
          <p className="text-xs text-slate-500 truncate">{d.label} <span className="text-[10px]">(archiviato)</span></p>
          <p className="text-xs text-slate-400 font-mono2 truncate">{Array.isArray(v[d.field_key]) ? v[d.field_key].join(", ") : String(v[d.field_key])}</p>
        </div>
      ))}
      {free.length > 0 && <p className="text-[11px] text-slate-500 pt-1">Campi liberi senza definizione (testo; creati da automazioni o manualmente):</p>}
      {free.map((k) => (
        <div key={k} className="flex items-center gap-2" data-testid={`cf-row-free-${k}`}>
          <span className="text-xs text-slate-400 w-32 sm:w-40 truncate shrink-0 font-mono2" title={k}>{k}</span>
          <Input value={Array.isArray(v[k]) ? v[k].join("; ") : v[k] ?? ""} onChange={(e) => onChange(k, e.target.value)} data-testid={`custom-field-${k}`} className={`${inp} flex-1`} />
          <button type="button" onClick={() => onRemove(k)} data-testid={`custom-field-remove-${k}`} className="text-slate-500 hover:text-[#EF4444] shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-1">
        <Input value={newCustom.key} onChange={(e) => setNewCustom({ ...newCustom, key: e.target.value })} data-testid="custom-field-new-key" placeholder="campo libero" className={`${inp} w-32 sm:w-40 shrink-0`} />
        <Input value={newCustom.value} onChange={(e) => setNewCustom({ ...newCustom, value: e.target.value })} data-testid="custom-field-new-value" placeholder="valore" className={`${inp} flex-1`} />
        <button type="button" onClick={onAdd} data-testid="custom-field-add" className="text-[#10B981] hover:text-[#059669] shrink-0 text-xl leading-none">+</button>
      </div>
    </div>
  );
}
